import { useTranslation } from "react-i18next"
import { graphql, useFragment } from "react-relay"
import { Clock, Warning } from "@phosphor-icons/react"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { WithTooltip } from "@/hoc/with-tooltip"
import { toast } from "@/hooks/useToast"
import { isFlatLength } from "@/lib/appointment-type-rules"
import { getServiceMapping } from "@/lib/fetch-service-mappings"
import { appointmentActions_appointment$key } from "@/modules/waiting-room/components/appointment-drawer/__generated__/appointmentActions_appointment.graphql"
import { useSelectNextAppointment } from "@/modules/waiting-room/contexts/appointment-context"
import { useRedefineAppointmentServiceMutation$data } from "@/modules/waiting-room/services/__generated__/useRedefineAppointmentServiceMutation.graphql"
import { useTryToReassignAgentMutation$data } from "@/modules/waiting-room/services/__generated__/useTryToReassignAgentMutation.graphql"
import {
  RedefineAppointmentServiceError,
  useRedefineAppointmentServiceMutation,
} from "@/modules/waiting-room/services/useRedefineAppointmentServiceMutation"
import {
  TryToReassignAgentError,
  useTryToReassignAgentMutation,
} from "@/modules/waiting-room/services/useTryToReassignAgentMutation"
import { useUpdateAppointmentFlexibilityMutation } from "@/modules/waiting-room/services/useUpdateAppointmentFlexibilityMutation"
import { useUpdateAppointmentUrgencyMutation } from "@/modules/waiting-room/services/useUpdateAppointmentUrgencyMutation"
import type { Flexibility, Service, Urgency } from "@/types/__generated__/union-types"

const AppointmentActionsFragment = graphql`
  fragment appointmentActions_appointment on Appointment {
    id
    urgency
    reviewStatus
    appointmentType
    service
    assignedTo
    isAutomaticallyReassignable
    patient {
      birthDate
      patientFileLock {
        lockedBy
      }
    }
  }
`

type AppointmentActionsProps = {
  children: React.JSX.Element
  appointmentRef: appointmentActions_appointment$key
  flexibility: Flexibility
}

const URGENCY_OPTIONS: { value: Urgency; translationKey: string; icon: React.JSX.Element }[] = [
  {
    value: "MEDICAL_ACUTE",
    translationKey: "scheduling.waitingRoom.priority.MEDICAL_ACUTE",
    icon: <Warning weight="fill" className="text-critical" />,
  },
  {
    value: "MEDICAL_URGENT",
    translationKey: "scheduling.waitingRoom.priority.MEDICAL_URGENT",
    icon: <Warning className="text-critical" />,
  },
  {
    value: "NORMAL",
    translationKey: "scheduling.waitingRoom.priority.NORMAL",
    icon: <span className="h-6 w-6"></span>,
  },
]

const FLEXIBILITY_OPTIONS: { value: Flexibility; translationKey: string; icon: React.JSX.Element }[] = [
  {
    value: "FIXED_TIME",
    translationKey: "scheduling.waitingRoom.priority.FIXED_TIME",
    icon: <Warning className="text-on-surface-subdued" />,
  },
  {
    value: "NOT_EARLIER",
    translationKey: "scheduling.waitingRoom.priority.NOT_EARLIER",
    icon: <Clock className="text-on-surface-subdued" />,
  },
  {
    value: "FLEXIBLE",
    translationKey: "scheduling.waitingRoom.priority.FLEXIBLE",
    icon: <span className="h-6 w-6"></span>,
  },
]

const SERVICE_OPTIONS: { value: Service; translationKey: string; icon: React.JSX.Element }[] = [
  {
    value: "CONSULTATION",
    translationKey: "scheduling.waitingRoom.service.CONSULTATION",
    icon: <span className="h-6 w-6"></span>,
  },
  {
    value: "ADMINISTRATIVE_REQUEST",
    translationKey: "scheduling.waitingRoom.service.ADMINISTRATIVE_REQUEST",
    icon: <span className="h-6 w-6"></span>,
  },
]

const AppointmentActions = ({ children, appointmentRef, flexibility }: AppointmentActionsProps) => {
  const { id, urgency, reviewStatus, appointmentType, service, assignedTo, isAutomaticallyReassignable, patient } =
    useFragment<appointmentActions_appointment$key>(AppointmentActionsFragment, appointmentRef)
  const { t } = useTranslation()
  const updateAppointmentUrgencyMutation = useUpdateAppointmentUrgencyMutation()
  const updateAppointmentFlexibilityMutation = useUpdateAppointmentFlexibilityMutation()
  const redefineAppointmentServiceMutation = useRedefineAppointmentServiceMutation()
  const tryToReassignAgentMutation = useTryToReassignAgentMutation()
  const selectNextAppointment = useSelectNextAppointment()

  const serviceItem = service === "ADMINISTRATIVE_REQUEST" ? "ADMINISTRATIVE_REQUEST" : "CONSULTATION"

  const isEmergencyService = getServiceMapping().serviceMappings.find(
    (mapping) => mapping.service === service,
  )?.isUrgent

  const currentSelectedUrgency = URGENCY_OPTIONS.find((option) => option.value === urgency)!
  const currentSelectedFlexibility = FLEXIBILITY_OPTIONS.find((option) => option.value === flexibility)!
  const currentSelectedServiceItem = SERVICE_OPTIONS.find((option) => option.value === serviceItem)!

  const isFlatLengthAppointment = isFlatLength(appointmentType)

  const isUpdateUrgencyDisabled = () => service === "ADMINISTRATIVE_REQUEST" || isFlatLengthAppointment

  const getTooltipForUpdateUrgency = () =>
    isFlatLengthAppointment
      ? t("scheduling.waitingRoom.priority.medicalUrgencyCannotBeChangedForFullHourServices")
      : t("scheduling.waitingRoom.priority.medicalUrgencyCannotBeChangedForAdminRequests")

  const isUpdateFlexibilityDisabled = () => isFlatLengthAppointment

  const isRedefineAppointmentServiceDisabled = () =>
    patient.patientFileLock.lockedBy || appointmentType === "FOLLOW_UP" || isFlatLengthAppointment

  const getTooltipForRedefineAppointmentService = () => {
    if (patient.patientFileLock.lockedBy) {
      return t("scheduling.waitingRoom.medicalRecordIsEditedByAnAgent")
    }

    if (appointmentType === "FOLLOW_UP") {
      return t("scheduling.waitingRoom.consultationTypeCannotBeChangedFollowUps")
    }

    if (isFlatLengthAppointment) {
      return t("scheduling.waitingRoom.service.serviceCannotBeChangedForFullHourServices")
    }

    return ""
  }

  const handleRedefineAppointmentServiceCompleted = (response: useRedefineAppointmentServiceMutation$data) => {
    const { errors } = response.redefineAppointmentService

    if (errors?.length) {
      const errorMessages: Record<RedefineAppointmentServiceError, string> = {
        AppointmentNotFound: t("scheduling.waitingRoom.errors.appointmentNotFound"),
        PatientFileIsLocked: t("scheduling.waitingRoom.errors.patientFileIsLocked"),
        FeatureIsDisabledDuringDayLightSavingTime: t(
          "scheduling.waitingRoom.errors.featureIsDisabledDuringDayLightSavingTime",
        ),
        ServiceDoesNotAllowTheConsultationType: t(
          "scheduling.waitingRoom.service.serviceCannotBeChangedForFullHourServices",
        ),
        "%other": "",
      }

      const errorDescription = errorMessages[errors[0].__typename]
      toast({ description: errorDescription })
      return
    }

    const formerAssignedPerson = !assignedTo ? t("scheduling.waitingRoom.nobody") : assignedTo
    const assignedPerson =
      response.redefineAppointmentService.appointment?.assignedTo ?? t("scheduling.waitingRoom.nobody")

    const changeDescription =
      formerAssignedPerson === assignedPerson
        ? t("scheduling.waitingRoom.appointmentWasChanged")
        : t("scheduling.waitingRoom.appointmentWasChangedFromTo", {
            formerAssignedPerson,
            assignedPerson,
          })

    toast({ description: changeDescription })
  }

  const handleTryToReassignAgentCompleted = (response: useTryToReassignAgentMutation$data) => {
    const { errors } = response.tryToReassignAgent

    if (errors?.length) {
      const errorMessages: Record<TryToReassignAgentError, string> = {
        AppointmentNotFound: t("scheduling.waitingRoom.errors.appointmentNotFound"),
        NoSuitableAgentFound: t("scheduling.waitingRoom.errors.noSuitableAgentFound"),
        AppointmentIsNotAutomaticallyReassignable: t(
          "scheduling.waitingRoom.errors.appointmentIsNotAutomaticallyReassignable",
        ),
        FeatureIsDisabledDuringDayLightSavingTime: t(
          "scheduling.waitingRoom.errors.featureIsDisabledDuringDayLightSavingTime",
        ),
        "%other": "",
      }

      const errorDescription = errorMessages[errors[0].__typename]
      toast({ description: errorDescription })
      return
    }

    const formerAssignedPerson = !assignedTo ? t("scheduling.waitingRoom.nobody") : assignedTo
    const assignedPerson = response.tryToReassignAgent.appointment?.assignedTo ?? t("scheduling.waitingRoom.nobody")

    const changeDescription = t("scheduling.waitingRoom.appointmentWasChangedFromTo", {
      formerAssignedPerson,
      assignedPerson,
    })

    toast({ description: changeDescription })
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild className="outline-hidden">
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        hideWhenDetached
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        align="end"
        data-testid="review-appointment-content"
        className="grid min-w-75 gap-0.5 rounded-lg border-none p-2 leading-5 transition-all duration-200">
        {isUpdateUrgencyDisabled() ? (
          <DropdownMenuSub>
            <WithTooltip text={getTooltipForUpdateUrgency()}>
              <DropdownMenuSubTrigger
                className="grid-cols-auto-1fr text-on-surface grid w-full cursor-not-allowed items-center gap-2 rounded-lg px-2 py-1 opacity-50"
                data-testid="preview-urgency-value"
                disabled={true}>
                {currentSelectedUrgency.icon}
                <div className="flex flex-col">
                  <div>{t(currentSelectedUrgency.translationKey)}</div>
                  <div className="text-on-surface-subdued text-xs">
                    {t("scheduling.waitingRoom.priority.medicalUrgency")}
                  </div>
                </div>
              </DropdownMenuSubTrigger>
            </WithTooltip>
          </DropdownMenuSub>
        ) : (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              className="grid-cols-auto-1fr text-on-surface hover:bg-surface-hovered! grid w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1"
              data-testid="preview-urgency-value">
              {currentSelectedUrgency.icon}
              <div className="flex flex-col">
                <div>{t(currentSelectedUrgency.translationKey)}</div>
                <div className="text-on-surface-subdued text-xs">
                  {t("scheduling.waitingRoom.priority.medicalUrgency")}
                </div>
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent className="w-67.5">
                {URGENCY_OPTIONS.map((option) => {
                  if (isEmergencyService && option.value === "NORMAL") {
                    return null
                  }

                  return (
                    <DropdownMenuCheckboxItem
                      data-testid={option.value.toLowerCase() + "-item"}
                      key={option.value}
                      className="gap-2 px-2 py-1"
                      onClick={(event) => {
                        event.stopPropagation()
                        updateAppointmentUrgencyMutation({
                          id,
                          urgency: option.value,
                          patientDateOfBirth: patient.birthDate,
                        })
                        if (reviewStatus !== "REVIEWED") {
                          selectNextAppointment()
                        }
                      }}
                      checked={urgency === option.value}>
                      {option.icon}
                      {t(option.translationKey)}
                    </DropdownMenuCheckboxItem>
                  )
                })}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        )}
        {isUpdateFlexibilityDisabled() ? (
          <DropdownMenuSub>
            <WithTooltip text={t("scheduling.waitingRoom.priority.flexibilityCannotBeChangedForFullHourServices")}>
              <DropdownMenuSubTrigger
                className="grid-cols-auto-1fr text-on-surface grid w-full cursor-not-allowed items-center gap-2 rounded-lg px-2 py-1 opacity-50"
                data-testid="preview-flexibility-value"
                disabled={true}>
                {currentSelectedFlexibility.icon}
                <div className="flex flex-col">
                  <div>{t(currentSelectedFlexibility.translationKey)}</div>
                  <div className="text-on-surface-subdued text-xs">
                    {t("scheduling.waitingRoom.priority.flexibility")}
                  </div>
                </div>
              </DropdownMenuSubTrigger>
            </WithTooltip>
          </DropdownMenuSub>
        ) : (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              className="grid-cols-auto-1fr text-on-surface hover:bg-surface-hovered! grid w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1"
              data-testid="preview-flexibility-value">
              {currentSelectedFlexibility.icon}
              <div className="flex flex-col">
                <div>{t(currentSelectedFlexibility.translationKey)}</div>
                <div className="text-on-surface-subdued text-xs">
                  {t("scheduling.waitingRoom.priority.flexibility")}
                </div>
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent className="w-67.5">
                {FLEXIBILITY_OPTIONS.map((option) => (
                  <DropdownMenuCheckboxItem
                    data-testid={option.value.toLowerCase() + "-item"}
                    key={option.value}
                    className="gap-2 px-2 py-1"
                    onClick={(event) => {
                      event.stopPropagation()
                      updateAppointmentFlexibilityMutation({
                        id,
                        flexibility: option.value,
                      })
                      if (reviewStatus !== "REVIEWED") {
                        selectNextAppointment()
                      }
                    }}
                    checked={flexibility === option.value}>
                    {option.icon}
                    {t(option.translationKey)}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        )}
        {isRedefineAppointmentServiceDisabled() ? (
          <DropdownMenuSub>
            <WithTooltip text={getTooltipForRedefineAppointmentService()}>
              <DropdownMenuSubTrigger
                className="grid-cols-auto-1fr text-on-surface hover:bg-surface-hovered! grid w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1"
                disabled={true}>
                {currentSelectedServiceItem.icon}
                <div className="flex flex-col">
                  <div>{t(currentSelectedServiceItem.translationKey)}</div>
                  <div className="text-on-surface-subdued text-xs">{t("scheduling.waitingRoom.service.service")}</div>
                </div>
              </DropdownMenuSubTrigger>
            </WithTooltip>
          </DropdownMenuSub>
        ) : (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="grid-cols-auto-1fr text-on-surface hover:bg-surface-hovered! grid w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1">
              {currentSelectedServiceItem.icon}
              <div className="flex flex-col">
                <div>{t(currentSelectedServiceItem.translationKey)}</div>
                <div className="text-on-surface-subdued text-xs">{t("scheduling.waitingRoom.service.service")}</div>
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent className="w-67.5">
                {SERVICE_OPTIONS.map((option) => (
                  <DropdownMenuCheckboxItem
                    data-testid={option.value.toLowerCase() + "-item"}
                    key={option.value}
                    className="gap-2 px-2 py-1"
                    onClick={(event) => {
                      event.stopPropagation()
                      if (option.value === serviceItem) {
                        return
                      }
                      redefineAppointmentServiceMutation(
                        {
                          id,
                          service: option.value,
                        },
                        handleRedefineAppointmentServiceCompleted,
                      )
                    }}
                    checked={serviceItem === option.value}>
                    {option.icon}
                    {t(option.translationKey)}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        )}
        <DropdownMenuItem
          className="grid-cols-auto-1fr text-on-surface hover:bg-surface-hovered! grid w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1"
          disabled={!isAutomaticallyReassignable}
          onClick={(event) => {
            event.stopPropagation()
            tryToReassignAgentMutation(
              {
                id,
              },
              handleTryToReassignAgentCompleted,
            )
          }}>
          <span className="h-6 w-6"></span>
          <div className="flex flex-col py-2">
            <div>{t("scheduling.waitingRoom.tryToReassignAgent")}</div>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { AppointmentActions }
